/**
 * workspaceContext — Team workspaces (V2 additive).
 *
 * ONE axios request interceptor, registered on BOTH api clients (apiService +
 * v2Service), that injects `workspace_org_id` into the JSON body of every
 * workspace-scoped endpoint while a team workspace is active. Existing call
 * sites stay untouched — the backend swaps the owner key ('org#<org_id>')
 * after validating the caller's membership.
 */
import type { InternalAxiosRequestConfig } from 'axios';
import { API_CONFIG } from './config';
import { useWorkspaceStore } from '@/store/workspaceStore';

const E = API_CONFIG.endpoints;

/** Endpoints whose reads/writes/credits follow the active workspace. */
const WORKSPACE_SCOPED_ENDPOINTS = new Set<string>([
  // Uploads — presign gate reads the org's paid tier so a free member of an
  // active paid team gets paid upload caps (not free-tier 10/day, 10 lifetime).
  E.fileUpload,
  // Projects + folders (V1 lambdas, org-aware additively)
  E.getFolders,
  E.createFolder,
  E.getFiles,
  // V2 resource libraries
  E.v2Assets,
  E.v2Brands,
  E.v2ListAvatars,
  E.v2ListVoices,
  E.v2CreateAvatar,
  E.v2CloneVoice,
  E.v2DesignVoice,
  // Generation (credits move to the org pool)
  E.v2AgentChat,
  E.v2CreateOrder,
  E.v2HeygenGenerate,
  // The LongVid twins are the same generation calls — without these, a team
  // member's order would be written to their personal account, not the org.
  E.v2LongvidChat,
  E.v2LongvidGenerate,
  // Same for the VideoGen twins — a routed team member's order belongs to the org.
  E.v2VideogenChat,
  E.v2VideogenGenerate,
]);

export function attachWorkspaceContext(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  const url = config.url || '';
  if (!WORKSPACE_SCOPED_ENDPOINTS.has(url)) return config;
  const orgId = useWorkspaceStore.getState().activeOrgId;
  if (!orgId) return config;
  if (config.data && typeof config.data === 'object' && !Array.isArray(config.data)) {
    config.data = { ...config.data, workspace_org_id: orgId };
  }
  return config;
}
